/** Small exact checkers with explicit domains. A successful process or a model's
 * verdict is never an input. Import recomputes results from retained witnesses.
 * These checks prove facts about these inputs; their application to a conjecture
 * is a separate, revision-bound mathematical obligation. */
export type RationalText = string
type Rational = readonly [bigint, bigint]

export interface LinearConstraint {
  id: string
  coefficients: RationalText[]
  relation: 'ge' | 'eq'
  rhs: RationalText
}
export interface ExactLinearSystem {
  variables: string[]
  constraints: LinearConstraint[]
}
export interface ExactCnf {
  variables: number
  clauses: number[][]
}
export interface ResolutionStep {
  left: number
  right: number
  pivot: number
}
export type MathCertificate =
  | { kind: 'linear-feasible'; system: ExactLinearSystem; point: RationalText[] }
  | { kind: 'linear-infeasible'; system: ExactLinearSystem; weights: RationalText[] }
  | { kind: 'boolean-satisfiable'; formula: ExactCnf; assignment: boolean[] }
  | {
      kind: 'boolean-feasible'
      system: ExactLinearSystem
      formula: ExactCnf
      assignment: boolean[]
    }
  | { kind: 'boolean-unsatisfiable'; formula: ExactCnf; steps: ResolutionStep[] }
  | {
      kind: 'relaxation-gap'
      system: ExactLinearSystem
      point: RationalText[]
      formula: ExactCnf
      steps: ResolutionStep[]
    }
  | {
      kind: 'nonrepresentable-point'
      system: ExactLinearSystem
      point: RationalText[]
      formula: ExactCnf
      /** A linear inequality valid for every Boolean solution but violated by
       * this feasible point. Exhaustive checking is explicitly bounded. */
      cut: LinearConstraint
    }

export interface MathCertificateResult {
  valid: boolean
  failure: 'malformed' | 'violation' | null
  checker: 'pathways-exact-mathematics-v1'
  checks: number
  fact: MathCertificate['kind'] | null
  reason: string
}
export const MATH_CERTIFICATE_LIMITS = {
  bytes: 1_000_000,
  variables: 128,
  constraints: 10_000,
  resolutionSteps: 20_000,
  exhaustiveVariables: 16,
  rationalDigits: 120,
  arithmeticDigits: 4096,
} as const

const abs = (n: bigint) => (n < 0n ? -n : n)
const gcd = (a: bigint, b: bigint): bigint => {
  while (b) [a, b] = [b, a % b]
  return abs(a)
}
const normalized = (a: bigint, b = 1n): Rational => {
  if (!b) throw new Error('A rational denominator is zero.')
  if (b < 0n) [a, b] = [-a, -b]
  const d = gcd(a, b)
  if (
    a.toString().length > MATH_CERTIFICATE_LIMITS.arithmeticDigits ||
    b.toString().length > MATH_CERTIFICATE_LIMITS.arithmeticDigits
  )
    throw new Error('Exact arithmetic exceeded its explicit size bound.')
  return [a / d, b / d]
}
const rational = (v: unknown): Rational => {
  if (
    typeof v !== 'string' ||
    v.length > MATH_CERTIFICATE_LIMITS.rationalDigits ||
    !/^-?\d+(?:\/[1-9]\d*)?$/.test(v)
  )
    throw new Error('Use exact integer or rational strings; decimals are not certificates.')
  const [a, b = '1'] = v.split('/')
  return normalized(BigInt(a!), BigInt(b))
}
const add = (a: Rational, b: Rational) => normalized(a[0] * b[1] + b[0] * a[1], a[1] * b[1])
const multiply = (a: Rational, b: Rational) => normalized(a[0] * b[0], a[1] * b[1])
const compare = (a: Rational, b: Rational) => a[0] * b[1] - b[0] * a[1]
const zero: Rational = [0n, 1n]
const dot = (a: Rational[], b: Rational[]) =>
  a.reduce((sum, value, i) => add(sum, multiply(value, b[i]!)), zero)
const integer = (n: unknown, max: number, min = 0): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max
class CertificateError extends Error {
  constructor(
    message: string,
    readonly failure: 'malformed' | 'violation',
  ) {
    super(message)
  }
}
const ensure = (
  condition: unknown,
  reason: string,
  failure: 'malformed' | 'violation' = 'malformed',
): void => {
  if (!condition) throw new CertificateError(reason, failure)
}
const vector = (v: unknown, length: number): Rational[] => {
  ensure(Array.isArray(v) && v.length === length, 'A certificate vector has the wrong dimension.')
  return (v as unknown[]).map(rational)
}
const constraint = (v: LinearConstraint, dimension: number) => {
  ensure(v && typeof v.id === 'string' && v.id.length > 0, 'A constraint needs an identifier.')
  ensure(v.relation === 'ge' || v.relation === 'eq', 'Unknown linear relation.')
  return { ...v, coefficients: vector(v.coefficients, dimension), rhs: rational(v.rhs) }
}
const system = (v: ExactLinearSystem) => {
  ensure(
    v &&
      Array.isArray(v.variables) &&
      v.variables.length <= MATH_CERTIFICATE_LIMITS.variables &&
      v.variables.every((n) => typeof n === 'string' && n.length > 0 && n.length <= 120) &&
      new Set(v.variables).size === v.variables.length &&
      Array.isArray(v.constraints) &&
      v.constraints.length <= MATH_CERTIFICATE_LIMITS.constraints,
    'Invalid or oversized linear system.',
  )
  const constraints = v.constraints.map((c) => constraint(c, v.variables.length))
  ensure(
    new Set(constraints.map((c) => c.id)).size === constraints.length,
    'Duplicate constraint id.',
  )
  return constraints
}
const cnf = (v: ExactCnf) => {
  ensure(
    v &&
      integer(v.variables, MATH_CERTIFICATE_LIMITS.variables) &&
      Array.isArray(v.clauses) &&
      v.clauses.length <= MATH_CERTIFICATE_LIMITS.constraints &&
      v.clauses.every(
        (c) =>
          Array.isArray(c) &&
          c.length <= 2 * v.variables &&
          c.every((l) => typeof l === 'number' && integer(Math.abs(l), v.variables, 1)) &&
          new Set(c).size === c.length,
      ),
    'Invalid or oversized Boolean formula.',
  )
  return v.clauses.map((c) => [...c])
}
const satisfies = (formula: ExactCnf, assignment: boolean[]) =>
  formula.clauses.every((c) => c.some((l) => assignment[Math.abs(l) - 1] === l > 0))

export function checkMathCertificate(value: unknown): MathCertificateResult {
  let checks = 0
  const answer = (
    valid: boolean,
    fact: MathCertificateResult['fact'],
    reason: string,
    failure: MathCertificateResult['failure'] = null,
  ) => ({
    valid,
    failure,
    checker: 'pathways-exact-mathematics-v1' as const,
    checks,
    fact,
    reason,
  })
  try {
    ensure(
      JSON.stringify(value).length <= MATH_CERTIFICATE_LIMITS.bytes,
      'Certificate is too large.',
    )
    const v = value as MathCertificate
    ensure(v && typeof v === 'object', 'A certificate must be an object.')
    const feasible = (s: ExactLinearSystem, p: RationalText[]) => {
      const rows = system(s)
      const point = vector(p, s.variables.length)
      for (const row of rows) {
        checks++
        const delta = compare(dot(row.coefficients, point), row.rhs)
        ensure(
          row.relation === 'eq' ? delta === 0n : delta >= 0n,
          `The point violates original constraint ${row.id}.`,
          'violation',
        )
      }
      return point
    }
    const unsatisfiable = (f: ExactCnf, steps: ResolutionStep[]) => {
      const clauses = cnf(f)
      ensure(
        Array.isArray(steps) && steps.length <= MATH_CERTIFICATE_LIMITS.resolutionSteps,
        'Invalid or oversized resolution derivation.',
      )
      for (const step of steps) {
        ensure(
          step &&
            integer(step.left, clauses.length - 1) &&
            integer(step.right, clauses.length - 1) &&
            integer(step.pivot, f.variables, 1),
          'A resolution step refers to an unavailable clause or pivot.',
        )
        const left = clauses[step.left]!
        const right = clauses[step.right]!
        ensure(
          left.includes(step.pivot) && right.includes(-step.pivot),
          'Resolution pivot mismatch.',
        )
        const resolvent = [
          ...new Set([
            ...left.filter((x) => x !== step.pivot),
            ...right.filter((x) => x !== -step.pivot),
          ]),
        ]
        ensure(
          !resolvent.some((x) => resolvent.includes(-x)),
          'A tautology is not a refutation step.',
        )
        clauses.push(resolvent)
        checks++
      }
      ensure(
        clauses.some((c) => c.length === 0),
        'The derivation never establishes the empty clause.',
      )
    }
    switch (v.kind) {
      case 'linear-feasible':
        feasible(v.system, v.point)
        break
      case 'linear-infeasible': {
        const rows = system(v.system)
        const weights = vector(v.weights, rows.length)
        const lhs = v.system.variables.map(() => zero)
        let rhs = zero
        rows.forEach((row, i) => {
          const weight = weights[i]!
          ensure(
            row.relation === 'eq' || weight[0] >= 0n,
            'An inequality has a negative multiplier.',
          )
          row.coefficients.forEach((a, j) => {
            lhs[j] = add(lhs[j]!, multiply(a, weight))
          })
          rhs = add(rhs, multiply(row.rhs, weight))
          checks++
        })
        ensure(
          lhs.every((n) => n[0] === 0n) && rhs[0] > 0n,
          'Multipliers do not establish 0 >= a positive rational.',
        )
        break
      }
      case 'boolean-feasible':
      case 'boolean-satisfiable':
        cnf(v.formula)
        ensure(
          Array.isArray(v.assignment) &&
            v.assignment.length === v.formula.variables &&
            v.assignment.every((x) => typeof x === 'boolean'),
          'Invalid Boolean assignment.',
        )
        ensure(
          satisfies(v.formula, v.assignment),
          'The assignment fails an original clause.',
          'violation',
        )
        checks += v.formula.clauses.length
        if (v.kind === 'boolean-feasible')
          feasible(
            v.system,
            v.assignment.map((b) => (b ? '1' : '0')),
          )
        break
      case 'boolean-unsatisfiable':
        unsatisfiable(v.formula, v.steps)
        break
      case 'relaxation-gap':
        feasible(v.system, v.point)
        ensure(
          v.system.variables.length === v.formula.variables,
          'The relaxation and Boolean formula have different variables.',
        )
        unsatisfiable(v.formula, v.steps)
        break
      case 'nonrepresentable-point': {
        const point = feasible(v.system, v.point)
        cnf(v.formula)
        ensure(
          v.formula.variables === point.length &&
            point.length <= MATH_CERTIFICATE_LIMITS.exhaustiveVariables,
          'Boolean cut validation exceeds its exact enumeration bound.',
        )
        const cut = constraint(v.cut, point.length)
        ensure(
          2 ** point.length * (v.formula.clauses.length + point.length + 1) <= 2_000_000,
          'Boolean cut validation exceeds its explicit work bound.',
        )
        let solutions = 0
        for (let bits = 0; bits < 2 ** point.length; bits++) {
          const assignment = point.map((_, i) => !!(bits & (1 << i)))
          if (!satisfies(v.formula, assignment)) continue
          solutions++
          const delta = compare(
            dot(
              cut.coefficients,
              assignment.map((b) => [b ? 1n : 0n, 1n]),
            ),
            cut.rhs,
          )
          ensure(
            cut.relation === 'eq' ? delta === 0n : delta >= 0n,
            'The proposed cut excludes a valid Boolean solution.',
          )
          checks++
        }
        ensure(
          solutions > 0,
          'Use a relaxation-gap certificate when there are no Boolean solutions.',
        )
        const delta = compare(dot(cut.coefficients, point), cut.rhs)
        ensure(
          cut.relation === 'eq' ? delta !== 0n : delta < 0n,
          'The cut does not exclude this point.',
        )
        break
      }
      default:
        throw new Error('Unknown certificate kind; no result was inferred.')
    }
    return answer(
      true,
      v.kind,
      'Exact checks passed on the retained inputs; application to a conjecture requires its interpretation and hypotheses.',
    )
  } catch (error) {
    return answer(
      false,
      null,
      error instanceof Error ? error.message : 'Invalid certificate.',
      error instanceof CertificateError ? error.failure : 'malformed',
    )
  }
}
